import { useMemo } from 'react';
import { useDevMode } from '../context/DevModeContext';
import * as ob from '../lib/obfuscate';

/**
 * useObfuscate
 *
 * Returns a set of helpers that obfuscate PII when dev mode is active, and pass
 * the value straight through otherwise. Use at display sites, e.g.
 *
 *   const priv = useObfuscate();
 *   <span>{priv.rego(vehicle.registration)}</span>
 *   <span>{priv.email(customer.email, customer.firstName)}</span>
 *
 * `active` is exposed so callers can branch on it directly when needed.
 */
export function useObfuscate() {
  const { active } = useDevMode();

  return useMemo(
    () => ({
      active,
      name: (v) => (active ? ob.obfuscateName(v) : v),
      firstName: (v) => (active ? ob.obfuscateFirstName(v) : v),
      lastName: (v) => (active ? ob.obfuscateLastName(v) : v),
      rego: (v) => (active ? ob.obfuscateRego(v) : v),
      vin: (v) => (active ? ob.obfuscateVin(v) : v),
      phone: (v) => (active ? ob.obfuscatePhone(v) : v),
      email: (v, nameHint) => (active ? ob.obfuscateEmail(v, nameHint) : v),
      jobNumber: (v) => (active ? ob.obfuscateJobNumber(v) : v),
      address: (v) => (active ? ob.obfuscateAddress(v) : v),
    }),
    [active],
  );
}

export default useObfuscate;
